import isNull from 'lodash/isNull'
import isString from 'lodash/isString'
import isUndefined from 'lodash/isUndefined'
import startsWith from 'lodash/startsWith'
import {ReactPropTypeLocationNames} from 'react'
import PropTypes from 'prop-types'

import FitStep from './types/FitStep'
import Height from './types/Height'
import JacketLength from './types/JacketLength'
import Preference from './types/Preference'
import Sizes from './types/Sizes'
import SurveyNumber from './types/SurveyNumber'
import SurveyValue from './types/SurveyValue'

const ANONYMOUS = 'ANONYMOUS'
const propTypeIsFilled = (props, propName) =>
  !isUndefined(props[propName]) && !isNull(props[propName])

// use to add .isRequired functionality to prop types with custom validators
const createChainableTypeChecker = (validate) => {
  const checkType = (
    isRequired,
    props,
    propName,
    componentName = ANONYMOUS,
    location,
    propFullName = propName,
  ) => {
    if (!propTypeIsFilled(props, propName) && isRequired) {
      throw new Error(
        `Required ${ReactPropTypeLocationNames[location]} \`${propFullName}\` was not specified ` +
          `in \`${componentName}\`.`,
      )
    } else {
      validate(props, propName, propFullName, componentName, location)
    }
  }

  const chainedCheckType = checkType.bind(null, false)
  chainedCheckType.isRequired = checkType.bind(null, true)

  return chainedCheckType
}

// simply create a propType that can be .isRequired
const createAwesomePropType = (validator) =>
  createChainableTypeChecker((props, propName, propFullName, componentName = ANONYMOUS) => {
    if (propTypeIsFilled(props, propName)) {
      const propValue = props[propName]

      validator(propValue, propFullName, componentName)
    }
  })

const checkClass = (Class) =>
  createAwesomePropType((propValue, propFullName, componentName) => {
    try {
      if (!(propValue instanceof Class)) {
        throw new Error(`${propFullName} in ${componentName} is not a ${Class.name}.`)
      }
    } catch (e) {
      throw new Error(`Error in checkClass: ${e.message}`)
    }
  })

export default {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  httpsUrl: createChainableTypeChecker(
    (props, propName, propFullName, componentName = ANONYMOUS) => {
      // checks that the propValue begins with 'https://'
      if (propTypeIsFilled(props, propName)) {
        const propValue = props[propName]

        if (!isString(propValue) || !startsWith(propValue, 'https://')) {
          throw new Error(`${propFullName} in ${componentName} is not an HTTPS URL.`)
        }
      }
    },
  ),
  FitStep: checkClass(FitStep),
  Height: checkClass(Height),
  image: PropTypes.shape({
    alt: PropTypes.string.isRequired,
    src: PropTypes.string.isRequired,
  }),
  JacketLength: checkClass(JacketLength),
  Preference: checkClass(Preference),
  Sizes: checkClass(Sizes),
  SurveyNumber: checkClass(SurveyNumber),
  SurveyValue: checkClass(SurveyValue),
  keyedArray: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.number.isRequired, PropTypes.string.isRequired]),
    }),
  ),
  responsiveBackgroundImage: PropTypes.shape({
    minWidth: PropTypes.number,
    src: PropTypes.string.isRequired,
  }),
  superText: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string.isRequired,
        minWidth: PropTypes.number,
      }),
    ),
  ]),
}
